import { Info, Target } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Card, CardContent } from "@/shared/ui/card";

import type { GrowthDiagnosis, MultiplierScore } from "../../api/bizAdvisorService";
import { BattleMapSection } from "./BattleMapSection";

function gradeColor(grade: string): string {
  if (grade === "优") return "bg-emerald-500";
  if (grade === "良") return "bg-blue-500";
  if (grade === "一般") return "bg-amber-500";
  return "bg-rose-500";
}

/** C2:分差箭头(↑绿/↓红;0 或缺位不渲染)。 */
function DeltaArrow({ delta }: { delta?: number | null }) {
  if (delta === null || delta === undefined || delta === 0) return null;
  const up = delta > 0;
  return (
    <span
      className={`tabular-nums text-xs font-medium ${up ? "text-emerald-600" : "text-rose-600"}`}
    >
      {up ? "↑" : "↓"} {Math.abs(delta)}
    </span>
  );
}

/** C4:行业水位 badge(above 绿 / below 红 / near 灰;"" 不渲染)。 */
function BenchmarkBadge({ vs }: { vs?: string }) {
  if (vs === "above")
    return (
      <Badge variant="outline" className="text-emerald-600">
        高于行业水位
      </Badge>
    );
  if (vs === "below")
    return (
      <Badge variant="outline" className="text-rose-600">
        低于行业水位
      </Badge>
    );
  if (vs === "near")
    return (
      <Badge variant="outline" className="text-muted-foreground">
        行业水位附近
      </Badge>
    );
  return null;
}

/** "2026-07-01T10:00:00" → "07-01"(非法/缺位返 "")。 */
function shortDate(iso?: string): string {
  if (!iso) return "";
  const m = iso.match(/^\d{4}-(\d{2}-\d{2})/);
  return m ? m[1] : "";
}

/** C+:「本次诊断参照」回显行(行业/竞品/标杆,均缺位不渲染)。 */
function ReferenceLine({ d }: { d: GrowthDiagnosis }) {
  const parts: string[] = [];
  if (d.industry) parts.push(`行业 ${d.industry}`);
  if (d.competitors && d.competitors.length > 0) parts.push(`竞品 ${d.competitors.join("、")}`);
  if (d.benchmark) parts.push(`标杆 ${d.benchmark}`);
  if (parts.length === 0) return null;
  return <div className="mt-2 text-xs text-muted-foreground">本次诊断参照:{parts.join(" · ")}</div>;
}

/** 能力雷达:五乘子 SVG 五边形(流量×转化×客单价×复购×口碑转介绍)。 */
function RadarChart({ multipliers }: { multipliers: MultiplierScore[] }) {
  const C = 120;
  const CY = 110;
  const R = 78;
  const n = multipliers.length || 1;
  const angle = (i: number) => ((-90 + (i * 360) / n) * Math.PI) / 180;
  const pt = (i: number, r: number): [number, number] => [
    C + r * Math.cos(angle(i)),
    CY + r * Math.sin(angle(i)),
  ];
  const poly = (frac: number) => multipliers.map((_, i) => pt(i, R * frac).join(",")).join(" ");
  const dataPoly = multipliers
    .map((m, i) => pt(i, R * Math.min(1, Math.max(0, m.score / 100))).join(","))
    .join(" ");

  return (
    <svg viewBox="0 0 240 230" className="mx-auto h-64 w-full max-w-sm" data-testid="biz-radar">
      <g className="text-border">
        {[1, 0.66, 0.33].map((f) => (
          <polygon key={f} points={poly(f)} fill="none" stroke="currentColor" strokeWidth={1} />
        ))}
        {multipliers.map((_, i) => {
          const [x, y] = pt(i, R);
          return (
            <line key={i} x1={C} y1={CY} x2={x} y2={y} stroke="currentColor" strokeWidth={1} />
          );
        })}
      </g>
      <polygon
        points={dataPoly}
        fill="rgba(99,102,241,0.18)"
        stroke="rgb(99,102,241)"
        strokeWidth={2}
      />
      {multipliers.map((m, i) => {
        const [x, y] = pt(i, R + 18);
        return (
          <text
            key={m.key}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-current text-2xs text-foreground"
          >
            {m.label} {Math.round(m.score)}
          </text>
        );
      })}
    </svg>
  );
}

export function DiagnosisPanel({
  diagnosis,
  diagnosisId,
}: {
  diagnosis: GrowthDiagnosis;
  diagnosisId?: number | null;
}) {
  const d = diagnosis;
  const lev = d.top_leverage;
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex items-center justify-between p-6">
          <div>
            <div className="text-sm text-muted-foreground">
              {d.brand || "该企业"} · {d.industry || "—"}
            </div>
            <div className="mt-1 flex items-baseline gap-2 text-4xl font-bold">
              <span>
                {d.overall_score}
                <span className="text-base font-normal text-muted-foreground"> / 100</span>
              </span>
              <DeltaArrow delta={d.overall_delta} />
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              经营能力体检(增长五乘子)
              {d.overall_delta !== null && d.overall_delta !== undefined && (
                <span>
                  {" "}
                  · 较上次 {d.overall_delta > 0 ? "+" : ""}
                  {d.overall_delta} 分
                  {shortDate(d.previous_measured_at) && `(${shortDate(d.previous_measured_at)})`}
                </span>
              )}
            </div>
            <ReferenceLine d={d} />
          </div>
          <Badge className={`${gradeColor(d.grade)} px-4 py-1 text-lg text-white`}>{d.grade}</Badge>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="p-6">
            <div className="mb-2 text-sm font-medium">能力雷达</div>
            <RadarChart multipliers={d.multipliers} />
          </CardContent>
        </Card>

        {lev && (
          <Card className="border-amber-300 bg-amber-50/60">
            <CardContent className="space-y-2 p-6">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-700">
                <Target className="h-4 w-4" /> 最高杠杆 · {lev.label}
              </div>
              <div className="text-3xl font-bold text-amber-700">
                {lev.score}
                <span className="text-sm font-normal text-amber-600/70"> / 100</span>
              </div>
              <p className="text-sm leading-relaxed text-amber-900/80">{lev.reason}</p>
            </CardContent>
          </Card>
        )}
      </div>

      <Card>
        <CardContent className="space-y-4 p-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">五乘子诊断</span>
            {d.benchmark_bucket && (
              <span className="text-xs text-muted-foreground">
                对照行业水位:{d.benchmark_bucket}(内置粗标 v1)
              </span>
            )}
          </div>
          {d.multipliers.map((m) => (
            <div key={m.key} className="border-b pb-3 last:border-0 last:pb-0">
              <div className="flex items-center gap-2">
                <span className="font-medium">{m.label}</span>
                <span className="tabular-nums text-sm text-muted-foreground">{m.score} 分</span>
                <DeltaArrow delta={m.delta} />
                <BenchmarkBadge vs={m.vs_benchmark} />
                {m.data_sufficiency === "low" && (
                  <Badge variant="outline" className="text-amber-600">
                    数据不足
                  </Badge>
                )}
                {lev?.multiplier === m.key && (
                  <Badge className="bg-amber-500 text-white">最高杠杆</Badge>
                )}
              </div>
              {m.gap && <div className="mt-1 text-sm text-muted-foreground">缺口:{m.gap}</div>}
              {m.evidence.length > 0 && (
                <div className="mt-1 text-xs text-muted-foreground">
                  依据:{m.evidence.join("；")}
                </div>
              )}
              {m.actions.length > 0 && (
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
                  {m.actions.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {d.summary && (
        <Card>
          <CardContent className="space-y-1 p-6">
            <div className="text-sm font-medium">教练综述</div>
            <p className="text-sm leading-relaxed text-muted-foreground">{d.summary}</p>
          </CardContent>
        </Card>
      )}

      <BattleMapSection battlePlan={d.battle_plan} diagnosisId={diagnosisId} />

      {d.honest_notes.length > 0 && (
        <div className="space-y-1 rounded-md bg-muted/50 p-4">
          {d.honest_notes.map((note, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{note}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
